import { useEffect, useRef, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Radio, LogOut, Mic, Activity, Signal, Users, Zap, Clock, ShieldAlert, Loader2, Check, Disc, Play, Download, Trash2, Sun, Moon, Terminal, Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { BroadcastEngine, broadcasterSupported } from "@/audio/BroadcastEngine";
import { Waveform } from "@/components/Waveform";
import { SpectrumAnalyzer } from "@/components/SpectrumAnalyzer";
import { LivePulse } from "@/components/LivePulse";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

function fmtUptime(s) {
  s = Math.floor(s || 0);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function Tile({ icon: Icon, label, value, unit, tone = "default", testid }) {
  const color = tone === "good" ? "text-emerald-400" : tone === "warn" ? "text-amber-400" : tone === "live" ? "text-live" : "text-zinc-100";
  return (
    <div className="rounded-xl border border-white/5 bg-[#18181B] p-4 vox-inset" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2 text-zinc-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className={`font-mono text-2xl ${color}`}>
        {value}
        {unit && <span className="ml-1 text-xs text-zinc-500">{unit}</span>}
      </div>
    </div>
  );
}

export default function Studio() {
  const { user, logout } = useAuth();
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [state, setState] = useState("stopped");
  const [meter, setMeter] = useState(0);
  const [bitrate, setBitrate] = useState(0);
  const [targetBitrate, setTargetBitrate] = useState(48000);
  const [gain, setGain] = useState(1.0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [health, setHealth] = useState({ listeners: 0, shed_events: 0, uptime: 0, peak_listeners: 0 });
  const [hostName, setHostName] = useState("");
  const [title, setTitle] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLightMode, setIsLightMode] = useState(() => localStorage.getItem("theme") === "light");
  const engineRef = useRef(null);

  useEffect(() => {
    if (isLightMode) {
      document.body.classList.add("light-mode");
      localStorage.setItem("theme", "light");
    } else {
      document.body.classList.remove("light-mode");
      localStorage.setItem("theme", "dark");
    }
  }, [isLightMode]);

  const supported = broadcasterSupported();

  const loadDevices = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const mics = devs.filter((d) => d.kind === "audioinput");
      setDevices(mics);
      if (mics[0] && !deviceId) setDeviceId(mics[0].deviceId || "default");
    } catch (e) { /* ignore */ }
  }, [deviceId]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/stats");
      setHistory(data.sessions || []);
    } catch (e) { /* ignore */ }
  }, []);

  const [showLogs, setShowLogs] = useState(false);
  const [systemLogs, setSystemLogs] = useState([]);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await api.get("/logs");
      setSystemLogs(data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadRecordings = useCallback(async () => {
    try {
      const { data } = await api.get("/recordings");
      setRecordings(data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadDevices();
    api.get("/channel").then((r) => { setHostName(r.data.host_name || ""); setTitle(r.data.now_playing || ""); });
    loadStats();
    loadRecordings();
    return () => { engineRef.current && engineRef.current.stop(); };
  }, []); // eslint-disable-line

  const live = state === "live" || state === "reconnecting";

  useEffect(() => {
    if (!live) return;
    const poll = async () => {
      try { const { data } = await api.get("/health"); setHealth(data); } catch (e) {}
    };
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [live]);

  useEffect(() => {
    if (!showLogs) return;
    loadLogs();
    const timer = setInterval(loadLogs, 2500);
    return () => clearInterval(timer);
  }, [showLogs, loadLogs]);

  const getToken = useCallback(async () => {
    const { data } = await api.post("/stream/token");
    return data.stream_token;
  }, []);

  const goLive = async () => {
    if (!supported) { toast.error("Broadcasting requires Chrome or Edge (WebCodecs)."); return; }
    try {
      // ensure mic permission + fresh labels
      const engine = new BroadcastEngine({
        getToken,
        onState: (s) => {
          setState(s);
          if (s === "live") toast.success("You are on air" + (isRecording ? " (Recording Active)" : ""));
          if (s === "reconnecting") toast.warning("Connection dropped — reconnecting…");
          if (s === "error") toast.error("Broadcast error");
        },
        onMeter: (level, br) => { setMeter(level); setBitrate(br); },
      });
      engineRef.current = engine;
      await engine.start(deviceId && deviceId !== "default" ? deviceId : undefined, targetBitrate, gain, isRecording);
      loadDevices();
    } catch (e) {
      console.error("Broadcast start error:", e);
      const msg = e.name === "NotAllowedError" ? "Microphone permission denied" : (e.message || "Could not start broadcast");
      toast.error(msg);
      setState("stopped");
    }
  };

  const endBroadcast = async () => {
    await engineRef.current?.stop();
    engineRef.current = null;
    setState("stopped");
    setMeter(0);
    setBitrate(0);
    toast("Broadcast ended");
    setTimeout(loadStats, 800);
    setTimeout(loadRecordings, 1500);
  };

  const deleteRecording = async (id) => {
    try {
      await api.delete(`/recordings/${id}`);
      toast.success("Recording deleted");
      loadRecordings();
    } catch (e) {
      toast.error("Could not delete recording");
    }
  };

  const deleteHistoryItem = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      toast.success("History item deleted");
      loadStats();
    } catch (e) {
      toast.error("Could not delete history item");
    }
  };

  const clearAllHistory = async () => {
    try {
      await api.delete("/history");
      toast.success("Broadcast history cleared");
      loadStats();
    } catch (e) {
      toast.error("Could not clear history");
    }
  };

  const saveMeta = async () => {
    try {
      await api.put("/channel", { now_playing: title, host_name: hostName });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) { toast.error("Could not save"); }
  };

  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const kbps = Math.round(bitrate / 1000);

  return (
    <div className="vox-noise relative min-h-screen w-full" data-testid="studio-page">
      <header className="relative z-10 flex items-center justify-between border-b border-white/5 px-6 py-5 sm:px-10">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors">
            <Radio className="h-4 w-4 text-live" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase">VOX·CAST</span>
          </Link>
          <span className="hidden text-sm text-zinc-600 sm:inline">Studio</span>
        </div>
        <div className="flex items-center gap-4">
          <LivePulse active={live} />
          {live && (
            <Button variant="ghost" onClick={endBroadcast} data-testid="end-broadcast-btn"
              className="rounded-full border border-live/30 text-live hover:bg-live/10 hover:text-live">
              End Broadcast
            </Button>
          )}
          <button
            onClick={() => setIsLightMode(!isLightMode)}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/10 transition-colors"
            title="Toggle High-Contrast Light Mode for Visually Impaired Users"
            data-testid="theme-toggle"
          >
            {isLightMode ? <Moon className="h-3.5 w-3.5 text-amber-500" /> : <Sun className="h-3.5 w-3.5 text-amber-400" />}
            <span className="font-mono text-[10px] uppercase font-semibold">{isLightMode ? "Dark" : "Light"}</span>
          </button>
          <button onClick={logout} data-testid="logout-btn" className="text-zinc-500 hover:text-zinc-200 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 py-8 sm:px-10">
        {!supported && (
          <div className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-300" data-testid="unsupported-banner">
            Broadcasting needs WebCodecs — please use Chrome or Edge on desktop/Android. Listeners can use any browser.
          </div>
        )}

        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Channel · main</p>
          <h1 className="mt-1 font-serif text-4xl tracking-tight sm:text-5xl">{hostName || "VOX-CAST"}</h1>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {/* Mic picker */}
          <div className="rounded-xl border border-white/5 bg-[#18181B] p-5 vox-inset md:col-span-1">
            <div className="mb-4 flex items-center gap-2 text-zinc-400">
              <Mic className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-widest">Input</span>
            </div>
            <Select value={deviceId} onValueChange={setDeviceId} disabled={live}>
              <SelectTrigger className="border-white/10 bg-[#27272A]" data-testid="mic-select">
                <SelectValue placeholder="Select microphone" />
              </SelectTrigger>
              <SelectContent>
                {devices.length === 0 && <SelectItem value="default">Default microphone</SelectItem>}
                {devices.map((d, i) => (
                  <SelectItem key={d.deviceId || i} value={d.deviceId || "default"}>
                    {d.label || `Microphone ${i + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="mt-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Bitrate</span>
                <span className="font-mono text-[10px] font-semibold text-emerald-400">{targetBitrate / 1000} kbps</span>
              </div>
              <Select value={String(targetBitrate)} onValueChange={(val) => setTargetBitrate(Number(val))} disabled={live}>
                <SelectTrigger className="border-white/10 bg-[#27272A]" data-testid="bitrate-select">
                  <SelectValue placeholder="Select bitrate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="32000">32 kbps (Female Voice Mono)</SelectItem>
                  <SelectItem value="48000">48 kbps (Female Voice HQ - Recommended)</SelectItem>
                  <SelectItem value="64000">64 kbps (Female Studio Voice Ultra)</SelectItem>
                  <SelectItem value="128000">128 kbps (Full Bandwidth)</SelectItem>
                  <SelectItem value="192000">192 kbps (Studio HD)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Input Boost / Gain</span>
                <span className="font-mono text-[10px] font-semibold text-amber-400">
                  {Math.round(gain * 100)}% ({gain >= 1 ? `+${((gain - 1) * 6).toFixed(1)}dB` : `${((gain - 1) * 6).toFixed(1)}dB`})
                </span>
              </div>
              <Slider
                value={[gain]}
                min={0}
                max={2.5}
                step={0.05}
                onValueChange={([val]) => {
                  setGain(val);
                  engineRef.current?.setGain(val);
                }}
                className="py-1.5 cursor-pointer"
                data-testid="gain-slider"
              />
            </div>

            <div className="mt-4 flex items-center justify-between rounded-lg border border-white/5 bg-[#27272A]/40 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Disc className={`h-3.5 w-3.5 ${isRecording ? "text-red-500 animate-pulse" : "text-zinc-500"}`} />
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Record Session</span>
              </div>
              <Switch
                checked={isRecording}
                onCheckedChange={setIsRecording}
                disabled={live}
                data-testid="record-toggle"
              />
            </div>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Level</span>
                <span className="font-mono text-[10px] text-zinc-500">{Math.round(meter * 100)}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[#27272A]" data-testid="input-meter">
                <div className="h-full rounded-full transition-[width] duration-75"
                  style={{ width: `${Math.min(100, meter * 100)}%`, background: meter > 0.85 ? "#FF3B30" : "linear-gradient(90deg,#34d399,#fbbf24)" }} />
              </div>
              <p className="mt-4 font-mono text-[10px] leading-relaxed text-zinc-500">
                FEMALE SPEECH DSP · HPF 145Hz → PRESENCE 3.2kHz → DE-ESSER 9kHz → COMP 3.5:1<br />OPUS VOIP MONO 48kHz · {targetBitrate / 1000}kbps
              </p>
            </div>
          </div>

          {/* Go Live / waveform */}
          <div className="flex flex-col rounded-xl border border-white/5 bg-[#18181B] p-6 vox-inset md:col-span-2">
            <div className="mb-4 flex items-center gap-2 text-zinc-400">
              <Activity className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-widest">Broadcast</span>
            </div>
            <div className="flex-1 h-28 flex items-center justify-center p-2 rounded-lg bg-[#121214]/60 border border-white/5">
              <SpectrumAnalyzer analyser={engineRef.current?.analyser} active={live} bars={42} />
            </div>
            <div className="mt-6 flex items-center justify-between gap-4">
              {!live ? (
                <button onClick={goLive} disabled={state === "connecting"} data-testid="go-live-btn"
                  className="flex items-center gap-3 rounded-full px-8 py-4 font-medium text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "#FF3B30", boxShadow: "0 0 40px rgba(255,59,48,0.35)" }}>
                  {state === "connecting" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Radio className="h-5 w-5" />}
                  {state === "connecting" ? "Connecting…" : "Go Live"}
                </button>
              ) : (
                <div className="flex items-center gap-3 font-mono text-sm text-live" data-testid="live-indicator">
                  <span className="h-2.5 w-2.5 rounded-full bg-live" style={{ boxShadow: "0 0 10px #FF3B30" }} />
                  {state === "reconnecting" ? "RECONNECTING…" : "LIVE"} · {fmtUptime(health.uptime)}
                </div>
              )}
              <div className="text-right">
                <p className="font-mono text-2xl text-zinc-100">{kbps || 0}<span className="ml-1 text-xs text-zinc-500">kbps</span></p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">bitrate</p>
              </div>
            </div>
          </div>

          {/* Health */}
          <div className="grid grid-cols-2 gap-4 md:col-span-1 md:grid-cols-1">
            <Tile icon={Users} label="Listeners" value={health.listeners} tone={health.listeners > 0 ? "good" : "default"} testid="health-listeners" />
            <Tile icon={Signal} label="Peak" value={health.peak_listeners} testid="health-peak" />
          </div>
        </div>

        {/* second row: title editor + more health */}
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-white/5 bg-[#18181B] p-5 vox-inset md:col-span-3">
            <div className="mb-4 flex items-center gap-2 text-zinc-400">
              <Zap className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-widest">Now Playing</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input value={hostName} onChange={(e) => setHostName(e.target.value)} placeholder="Host / channel name"
                data-testid="host-name-input" className="border-white/10 bg-[#27272A]" />
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Broadcast title (e.g. Late Night Talk)"
                data-testid="title-input" className="border-white/10 bg-[#27272A]" />
            </div>
            <Button onClick={saveMeta} data-testid="save-meta-btn"
              className="mt-4 rounded-full bg-white text-black hover:bg-zinc-200">
              {savedFlash ? <><Check className="mr-1 h-4 w-4" /> Saved</> : "Update"}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 md:col-span-1 md:grid-cols-1">
            <Tile icon={Clock} label="Uptime" value={fmtUptime(health.uptime)} tone="default" testid="health-uptime" />
            <Tile icon={ShieldAlert} label="Shed" value={health.shed_events} tone={health.shed_events > 0 ? "warn" : "default"} testid="health-shed" />
          </div>
        </div>

        {/* history */}
        <div className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Broadcast History</p>
            {history.length > 0 && (
              <button
                onClick={clearAllHistory}
                className="font-mono text-xs text-zinc-500 hover:text-red-400 transition-colors flex items-center gap-1"
                data-testid="clear-history-btn"
              >
                <Trash2 className="h-3 w-3" /> Clear History
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-zinc-600" data-testid="history-empty">No past broadcasts yet.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-white/5" data-testid="history-table">
              {history.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b border-white/5 bg-[#18181B] px-5 py-3 last:border-0">
                  <div>
                    <p className="text-sm text-zinc-200">{s.now_playing || "Untitled session"}</p>
                    <p className="font-mono text-[10px] text-zinc-600">{s.started_at ? new Date(s.started_at).toLocaleString() : ""}</p>
                  </div>
                  <div className="flex items-center gap-4 font-mono text-xs text-zinc-500">
                    <span>peak {s.peak_listeners}</span>
                    <span>{fmtUptime(s.duration)}</span>
                    <span className={s.ended_at ? "text-zinc-600" : "text-live"}>{s.ended_at ? "ended" : "live"}</span>
                    <button
                      onClick={() => deleteHistoryItem(s.id)}
                      className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                      title="Delete history item"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recordings Archive */}
        <div className="mt-8 mb-12">
          <div className="mb-4 flex items-center justify-between">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Recorded Broadcasts Archive</p>
            <span className="font-mono text-xs text-zinc-600">{recordings.length} recordings</span>
          </div>
          {recordings.length === 0 ? (
            <p className="text-sm text-zinc-600" data-testid="recordings-empty">
              No recorded broadcasts saved. Enable "Record Session" before going live to archive broadcasts.
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-white/5" data-testid="recordings-table">
              {recordings.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 bg-[#18181B] px-5 py-3.5 last:border-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <Disc className="h-3.5 w-3.5 text-red-500" />
                      <p className="text-sm font-medium text-zinc-200">{r.title || "Recorded Session"}</p>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[10px] text-zinc-400">
                      <span>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                      <span>·</span>
                      <span>{r.host_name}</span>
                      <span>·</span>
                      <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-400 font-semibold border border-emerald-500/30">
                        ⚡ {Math.round((r.bitrate_bps || (r.size_bytes * 8 / Math.max(1, r.duration))) / 1000)} kbps
                      </span>
                      <span className="rounded bg-zinc-800/80 px-1.5 py-0.5 text-zinc-300 border border-white/5">
                        🎙️ 48 kHz Opus
                      </span>
                      {r.peak_listeners > 0 && (
                        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-400 border border-amber-500/30">
                          👥 peak {r.peak_listeners}
                        </span>
                      )}
                      <span>·</span>
                      <span>{(r.size_bytes / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5 font-mono text-xs">
                    <span className="text-zinc-400 mr-1">{fmtUptime(r.duration)}</span>
                    <a
                      href={`${API}/recordings/${r.id}/audio`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs text-zinc-200 hover:bg-white/20 transition-colors"
                    >
                      <Play className="h-3 w-3 text-emerald-400" /> Listen
                    </a>
                    <a
                      href={`${API}/recordings/${r.id}/mp3`}
                      download={`broadcast_${r.id}.mp3`}
                      className="flex items-center gap-1.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 px-3 py-1 text-xs hover:bg-purple-500/30 transition-colors"
                      title="Download as MP3 file"
                    >
                      <Download className="h-3 w-3 text-purple-400" /> MP3
                    </a>
                    <a
                      href={`${API}/recordings/${r.id}/audio`}
                      download={`broadcast_${r.id}.opus`}
                      className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs text-zinc-200 hover:bg-white/20 transition-colors"
                      title="Download original Opus file"
                    >
                      <Download className="h-3 w-3 text-amber-400" /> Opus
                    </a>
                    <button
                      onClick={() => deleteRecording(r.id)}
                      className="flex items-center gap-1 rounded-full p-1 text-zinc-500 hover:text-red-400 transition-colors"
                      title="Delete recording"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* System Diagnostics & Logs */}
        <div className="mt-8 mb-16">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-emerald-400" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">System Diagnostics & Logs</p>
            </div>
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="font-mono text-xs text-zinc-400 hover:text-zinc-100 transition-colors flex items-center gap-1.5 rounded-full border border-white/10 bg-[#18181B] px-3 py-1"
              data-testid="toggle-logs-btn"
            >
              <Terminal className="h-3 w-3 text-amber-400" />
              {showLogs ? "Hide Console" : "View Live Diagnostics Logs"}
            </button>
          </div>

          {showLogs && (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-4 font-mono text-xs shadow-2xl" data-testid="system-logs-panel">
              <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
                  <span className="text-zinc-400 text-[11px]">Streaming Server Logs ({systemLogs.length} entries)</span>
                </div>
                <button
                  onClick={() => {
                    const text = systemLogs.map((l) => `[${l.timestamp}] [${l.level}] ${l.name}: ${l.message}`).join("\n");
                    navigator.clipboard.writeText(text);
                    toast.success("Logs copied to clipboard!");
                  }}
                  className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-100 transition-colors"
                >
                  <Copy className="h-3 w-3" /> Copy Logs
                </button>
              </div>

              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-2 text-[11px] scrollbar-thin">
                {systemLogs.length === 0 ? (
                  <p className="text-zinc-600 italic">No system logs recorded yet.</p>
                ) : (
                  systemLogs.map((l, i) => (
                    <div key={i} className="flex flex-wrap items-start gap-2 leading-relaxed border-b border-white/5 pb-1 last:border-0">
                      <span className="text-zinc-600 shrink-0">{l.timestamp}</span>
                      <span
                        className={`font-semibold shrink-0 uppercase px-1 rounded text-[9px] ${
                          l.level === "ERROR"
                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                            : l.level === "WARNING" || l.level === "WARN"
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                            : "bg-emerald-500/15 text-emerald-400"
                        }`}
                      >
                        {l.level}
                      </span>
                      <span className="text-zinc-500 shrink-0">[{l.name}]</span>
                      <span className={l.level === "ERROR" ? "text-red-300" : l.level === "WARNING" ? "text-amber-200" : "text-zinc-300"}>
                        {l.message}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Footer Version */}
          <footer className="mt-8 border-t border-white/5 pt-4 pb-2 text-center text-[10px] font-mono text-zinc-600 tracking-wider">
            v1.8.1 (updated 2026-11-04 20:47)
          </footer>
        </div>
      </main>
    </div>
  );
}
